import type { FC, FocusEvent, ReactNode } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { ArrowRight, ArrowUpRight, ChevronDown, Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { Logo } from '@/components/generals/logo'
import { Eyebrow } from '@/components/generals/eyebrow'
import { ButtonLink } from '@/components/generals/button'
import { TransitionLink } from '@/components/generals/transition-link'
import { EmergencyBar, EMERGENCY_BAR_HEIGHT } from '@/components/generals/emergency-bar'
import { MobileMenu } from '@/components/generals/mobile-menu'
import { mainNav, QUOTE_PATH } from '@/constants/site'
import { services } from '@/constants/services'

interface HeaderProps {
    theme?: 'dark' | 'light'
}

export const Header: FC<HeaderProps> = ({ theme = 'light' }): ReactNode => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [megaOpen, setMegaOpen] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

    useEffect(() => {
        let lastY = window.scrollY
        let ticking = false

        const update = () => {
            const y = window.scrollY

            setScrolled(y > 12)

            if (y > lastY + 6 && y > 560) setHidden(true)
            else if (y < lastY - 6 || y <= 560) setHidden(false)

            lastY = y
            ticking = false
        }

        const handleScroll = () => {
            if (ticking) return

            ticking = true
            window.requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', handleScroll, { passive: true })

        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        const close = () => {
            setMegaOpen(false)
            setMenuOpen(false)
        }

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router])

    useEffect(() => () => clearTimeout(closeTimer.current), [])

    const openMega = () => {
        clearTimeout(closeTimer.current)
        setMegaOpen(true)
    }

    const scheduleClose = () => {
        clearTimeout(closeTimer.current)
        closeTimer.current = setTimeout(() => setMegaOpen(false), 140)
    }

    const handleMegaBlur = (event: FocusEvent<HTMLElement>) => {
        const next = event.relatedTarget as Node | null

        if (!next || !document.getElementById('site-header')?.contains(next)) scheduleClose()
    }

    const closeMenu = useCallback(() => setMenuOpen(false), [])

    const solid = scrolled || megaOpen
    const lightText = theme === 'dark' && !solid
    const isHidden = hidden && !megaOpen && !menuOpen
    const offset = isHidden ? 'translateY(-100%)' : scrolled ? `translateY(-${EMERGENCY_BAR_HEIGHT}px)` : 'translateY(0)'

    const isActive = (href: string): boolean => router.pathname === href || router.pathname.startsWith(`${href}/`)

    const navLinkClass = (href: string): string =>
        cn(
            'relative inline-flex items-center gap-1 rounded-full px-2.5 py-2 text-[14.5px] xl:px-3.5 font-medium tracking-[-0.01em] transition-colors duration-300',
            'after:absolute after:inset-x-2.5 after:bottom-1 xl:after:inset-x-3.5 after:h-px after:origin-right after:scale-x-0 after:bg-current after:transition-transform after:duration-500 after:ease-expo hover:after:origin-left hover:after:scale-x-100',
            lightText ? 'text-cream/85 hover:text-cream' : 'text-ink/75 hover:text-ink',
            isActive(href) && (lightText ? 'text-cream after:scale-x-100' : 'text-ink after:scale-x-100')
        )

    return (
        <>
            <div
                className='fixed inset-x-0 top-0 z-50 transition-transform duration-500 ease-expo'
                style={{ transform: offset }}
            >
                <EmergencyBar />

                <header
                    id='site-header'
                    onMouseLeave={scheduleClose}
                    className={cn(
                        'relative border-b transition-[background-color,border-color] duration-300',
                        solid ? 'border-ink/[0.08] bg-cream/85 backdrop-blur-xl' : 'border-transparent bg-transparent'
                    )}
                >
                    <Container className='flex h-[72px] items-center justify-between gap-6'>
                        <TransitionLink href='/' aria-label="Liftline home" className='shrink-0'>
                            <Logo tone={lightText ? 'light' : 'dark'} />
                        </TransitionLink>

                        <nav aria-label="Main" className='hidden lg:block'>
                            <ul className='flex items-center gap-0.5'>
                                {mainNav.map(link => link.href === '/services' ? (
                                    <li key={link.href} onMouseEnter={openMega} onFocus={openMega} onBlur={handleMegaBlur}>
                                        <TransitionLink href={link.href} className={navLinkClass(link.href)}>
                                            {link.label}

                                            <ChevronDown
                                                className={cn('size-3.5 transition-transform duration-300', megaOpen && 'rotate-180')}
                                                aria-hidden='true'
                                            />
                                        </TransitionLink>
                                    </li>
                                ) : (
                                    <li key={link.href} onMouseEnter={scheduleClose}>
                                        <TransitionLink href={link.href} className={navLinkClass(link.href)}>
                                            {link.label}
                                        </TransitionLink>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <div className='flex items-center gap-2'>
                            <ButtonLink href={QUOTE_PATH} size='sm' className='hidden sm:inline-flex'>
                                Get your price
                            </ButtonLink>

                            <button
                                type='button'
                                onClick={() => setMenuOpen(true)}
                                aria-label="Open menu"
                                aria-expanded={menuOpen}
                                className={cn(
                                    'flex size-11 items-center justify-center rounded-full border transition-colors lg:hidden',
                                    lightText ? 'border-cream/25 text-cream hover:bg-cream/10' : 'border-ink/15 text-ink hover:bg-ink/5'
                                )}
                            >
                                <Menu className='size-5' />
                            </button>
                        </div>
                    </Container>

                    <div
                        onMouseEnter={openMega}
                        onMouseLeave={scheduleClose}
                        onFocus={openMega}
                        onBlur={handleMegaBlur}
                        className={cn(
                            'absolute inset-x-0 top-full hidden pt-2 transition-[opacity,transform,translate,scale,rotate,visibility] duration-500 ease-expo lg:block',
                            megaOpen ? 'visible translate-y-0 opacity-100' : 'pointer-events-none invisible -translate-y-2 opacity-0'
                        )}
                    >
                        <Container>
                            <div className='grid grid-cols-12 gap-3 rounded-[28px] border border-ink/[0.08] bg-paper p-3 shadow-[0_40px_90px_-40px_rgba(21,23,26,0.5)]'>
                                <ul className='col-span-8 grid grid-cols-2 gap-1'>
                                    {services.map(service => (
                                        <li key={service.slug}>
                                            <TransitionLink
                                                href={service.href}
                                                className='group/item flex items-center gap-4 rounded-2xl p-3 transition-colors duration-300 hover:bg-sand/70'
                                            >
                                                <span className='relative size-14 shrink-0 overflow-hidden rounded-xl bg-sand'>
                                                    <Image
                                                        src={service.image}
                                                        alt=''
                                                        fill
                                                        sizes='56px'
                                                        className='object-cover transition-transform duration-700 ease-expo group-hover/item:scale-110'
                                                    />
                                                </span>

                                                <span className='min-w-0 flex-1'>
                                                    <span className='block truncate font-heading text-[17px] font-semibold tracking-[-0.025em] text-ink'>
                                                        {service.title}
                                                    </span>

                                                    <span className='mt-1 block truncate font-mono text-[10.5px] uppercase tracking-[0.12em] text-slate'>
                                                        {service.meta} · {service.priceLabel}
                                                    </span>
                                                </span>

                                                <ArrowUpRight
                                                    className='size-4 shrink-0 -translate-x-1 text-ember opacity-0 transition-all duration-300 group-hover/item:translate-x-0 group-hover/item:opacity-100'
                                                    aria-hidden='true'
                                                />
                                            </TransitionLink>
                                        </li>
                                    ))}
                                </ul>

                                <div className='relative col-span-4 flex flex-col justify-between overflow-hidden rounded-[22px] bg-ink p-7 text-cream'>
                                    <div className='door-grooves pointer-events-none absolute inset-0 opacity-50' aria-hidden='true' />

                                    <div className='relative'>
                                        <Eyebrow tone='light'>Door check</Eyebrow>

                                        <p className='mt-5 font-heading text-[1.65rem] font-semibold leading-[1.05] tracking-[-0.035em]'>
                                            Can't tell what broke?
                                        </p>

                                        <p className='mt-3 text-sm leading-relaxed text-cream/65'>
                                            Tell us what the door is doing. In about 30 seconds you'll see the likely fix.
                                        </p>
                                    </div>

                                    <div className='relative mt-8 flex flex-col items-start gap-4'>
                                        <ButtonLink href='/#diagnose' variant='light' size='sm'>
                                            Find the problem
                                        </ButtonLink>

                                        <TransitionLink
                                            href='/services'
                                            className='inline-flex items-center gap-1.5 text-sm text-cream/70 transition-colors hover:text-cream'
                                        >
                                            Browse every service
                                            <ArrowRight className='size-3.5' aria-hidden='true' />
                                        </TransitionLink>
                                    </div>
                                </div>
                            </div>
                        </Container>
                    </div>
                </header>
            </div>

            <MobileMenu open={menuOpen} onClose={closeMenu} />
        </>
    )
}
